import React, { useMemo, useState } from 'react';
import { Checkbox } from '@repo/common';
import { JOB_LABEL, assigneesOf, byJob, cn, isLeader, projectDeadline } from './model/derive';
import FileDrop, { uploadAttachments } from './FileDrop';
import { formatDue, fromInputValue, toInputValue, tzShortName } from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import {
  Field,
  Modal,
  ModalButton,
  PersonSelect,
  RadioGroup,
  Select,
  TextArea,
  TextInput,
} from './Modal';
import TdButton from './Button';
import TaskListEditor from './TaskListEditor';
import DateTimeField from './DateTimeField';
import TaskListGroups from './TaskListGroups';
import { OnePageFields, SlotFields, filledSlots, initialSlotRows, slotRowsOk } from './SlotFields';
import {
  HO_SLOTS,
  LINK_KINDS,
  ONE_PAGE,
  hoOf,
  kindRule,
  REQUIRED_SLOTS,
  missingRequired,
  slotCheck,
} from './model/handoffSlots';
import { SOURCE_LABEL, listOf, rejectReasonsFor, scopeRule } from './model/taskList';
import { KST, formatClock } from './model/time';
import useRowMotion from './useRowMotion';
import { LinkCheckPanel, isBlocking } from './LinkCheck';
import './motion.css';

/**
 * 태스크 상세의 행동 모달들. 필수 입력은 MVP §5 상태 전이표를 따른다.
 * 모두 지금은 화면 상태만 바꾼다 (model/actions.js).
 */

const useCommon = (task) => {
  const ctx = useWorkboardCtx();
  const members = useMemo(() => [...ctx.memberById.values()].sort(byJob), [ctx.memberById]);
  const tz = tzShortName(ctx.displayTz, ctx.now);
  return { ...ctx, members, tz, due: dueInfo(ctx, task).effective };
};

const Footer = ({ onClose, label, disabled, onConfirm }) => (
  <>
    <ModalButton onClick={onClose}>Cancel</ModalButton>
    <ModalButton primary disabled={disabled} onClick={onConfirm}>
      {label}
    </ModalButton>
  </>
);

const expectedCapOf = (due, now, data) => (due > now ? due : projectDeadline(data.project));

/**
 * 수락 → 진행 중. 예상 제출 시각은 지금 이후 ~ 마감 이전 (Task Flows W2_AcceptModal). 범위 밖이면 빨간 안내를 띄우고 Accept 를 막는다.
 * 수락 = Task List 확인 — 리스트 전체를 함께 보여 준다.
 */
export const AcceptModal = ({ task, onClose }) => {
  const { dispatch, now, displayTz, tz, data, toast, due, memberById } = useCommon(task);
  const [value, setValue] = useState('');
  const start = new Date(Math.max(now.getTime(), new Date(data.project.startAt ?? now).getTime()));
  const end = expectedCapOf(due, now, data);
  const list = listOf(task, memberById);
  const at = fromInputValue(value, displayTz);
  // 지금 이후 ~ 마감 이전 (마감과 같거나 늦으면 거부 · 기한 준수 규칙 §10-1)
  const ok = at && at > start && at < end;
  const error = !value
    ? ''
    : !at
      ? 'Enter a valid date and time.'
      : at <= start
        ? 'Pick a time later than now.'
        : at >= end
          ? 'Pick a time before the due time.'
          : '';
  const confirm = () => {
    dispatch({
      type: 'accept',
      taskId: task.id,
      expectedSubmitAt: at.toISOString(),
      at: now.toISOString(),
    });
    toast?.({ title: 'Task accepted', sub: `Expected submit ${formatDue(at, displayTz)} ${tz}` });
    onClose();
  };
  return (
    <Modal
      title='Accept task'
      subtitle={task.title}
      onClose={onClose}
      footer={
        <>
          {!value && (
            <span className='mr-auto text-[13px] text-text-secondary'>
              Pick an expected submit time
            </span>
          )}
          <Footer onClose={onClose} label='Accept' disabled={!ok} onConfirm={confirm} />
        </>
      }
    >
      <p className='text-[14px] leading-[150%] text-text-secondary'>
        When do you expect to submit? Your leader and the next person see this time.
      </p>
      <div className='flex flex-col gap-1.5'>
        <span className='text-[13px] font-medium text-text-primary'>Task List</span>
        <span className='text-[12px] text-text-secondary'>
          Accepting means you’ve read the whole Task List.
        </span>
        <TaskListGroups type={list.type} base={list.base} items={list.items} />
      </div>
      <Field
        label='Expected submit time'
        required
        htmlFor='accept-time'
        hint={
          error ? undefined : `Only times before the due time (${formatDue(end, displayTz)} ${tz}).`
        }
      >
        <DateTimeField
          id='accept-time'
          autoFocus
          value={value}
          // 지금보다 늦어야 한다 — 고를 수 있는 첫 시간은 지금 + 1분부터
          min={toInputValue(new Date(start.getTime() + 60_000), displayTz)}
          // 마감 1분 전까지 — 마감과 같은 시각은 고를 수 없다
          max={toInputValue(new Date(end.getTime() - 60_000), displayTz)}
          invalid={!!error}
          onChange={setValue}
        />
        {error && (
          <p
            id='accept-time-error'
            role='alert'
            className='text-[12px] leading-[140%] text-functional-failed04'
          >
            {error}
          </p>
        )}
      </Field>
    </Modal>
  );
};

/** 거절 — 사유 필수 (MVP #1) */
export const DeclineModal = ({ task, onClose }) => {
  const { dispatch, now, toast } = useCommon(task);
  const [reason, setReason] = useState('');
  const confirm = () => {
    dispatch({ type: 'decline', taskId: task.id, reason: reason.trim(), at: now.toISOString() });
    toast?.({ title: 'Task declined', sub: 'Your leader gets notified' });
    onClose();
  };
  return (
    <Modal
      title='Decline task'
      subtitle={task.title}
      onClose={onClose}
      footer={
        <Footer onClose={onClose} label='Decline' disabled={!reason.trim()} onConfirm={confirm} />
      }
    >
      <Field
        label='Reason'
        required
        htmlFor='decline-reason'
        hint='Your leader sees this and reassigns the task.'
      >
        <TextArea
          id='decline-reason'
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

/** 체크박스 행 — 보내는 사람의 Task List ✓ (공용 Checkbox) */
export const CheckRow = ({ label, checked, onChange }) => (
  <Checkbox
    checked={checked}
    onChange={onChange}
    label={label}
    className='h-auto items-start py-1.5'
    boxClassName='mt-[1px] shrink-0'
    labelClassName='text-[14px] leading-[150%] text-text-primary'
  />
);

const blankLink = () => ({ url: '', kind: LINK_KINDS[0] });

const withScheme = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const guessKind = (url) =>
  /figma\.com/i.test(url)
    ? 'Figma'
    : /github\.com/i.test(url)
      ? 'GitHub'
      : /notion\.(so|site)/i.test(url)
        ? 'Notion'
        : 'Web';

const prevLinks = (d) => (d.links?.length ? d.links : d.link ? [d.link] : []).filter(Boolean);

const prevFreeLinks = (d) => {
  const urls = prevLinks(d);
  if (!urls.length) return [blankLink()];
  return urls.map((url, i) => {
    const kind = d.linkKinds?.[i];
    return { url: withScheme(url), kind: LINK_KINDS.includes(kind) ? kind : guessKind(url) };
  });
};

const prevSlotRows = (d, slots, required) => {
  if (!slots) return [];
  const matched = (d.slots ?? [])
    .filter((s) => s.url && slots.some(([k]) => k === s.k))
    .map((s) => ({ tag: s.k, url: withScheme(s.url) }));
  const rows = matched.length
    ? matched
    : prevLinks(d).map((url, i) => ({
        tag: slots[Math.min(i, slots.length - 1)][0],
        url: withScheme(url),
      }));
  required
    .filter((k) => !rows.some((r) => r.tag === k))
    .forEach((k) => rows.push({ tag: k, url: '' }));
  return rows.length ? rows : initialSlotRows(slots);
};

/** 결과물 링크 여러 개 — 번호 · 링크 종류 · "+ Add link" · 행 삭제 */
const LinksEditor = ({ links, onChange, disabled }) => {
  const rows = links.length ? links : [blankLink()];
  const set = (i, patch) => onChange(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const motion = useRowMotion(rows, onChange);
  return (
    <div className='flex flex-col gap-2'>
      <ol className='flex flex-col gap-2'>
        {rows.map((l, i) => {
          const error = slotCheck(kindRule(l.kind), l.url);
          const inputId = i === 0 ? 'submit-link' : `submit-link-${motion.keys[i]}`;
          return (
            <li
              key={motion.keys[i]}
              className={cn('flex flex-col gap-1', motion.isFresh(motion.keys[i]) && 'td-rise')}
            >
              <div className='flex items-center gap-1.5'>
                <span className='w-5 shrink-0 text-right text-[13px] tabular-nums text-text-secondary'>
                  {i + 1}.
                </span>
                <Select
                  aria-label='Link type'
                  value={l.kind}
                  disabled={disabled}
                  onChange={(e) => set(i, { kind: e.target.value })}
                  className='w-[132px] shrink-0'
                >
                  {LINK_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </Select>
                <TextInput
                  id={inputId}
                  aria-label={`Deliverable link ${i + 1}`}
                  placeholder='https://'
                  value={l.url}
                  disabled={disabled}
                  maxLength={1000}
                  aria-invalid={!!error}
                  aria-describedby={error ? `${inputId}-error` : undefined}
                  onChange={(e) => set(i, { url: e.target.value })}
                  className={cn('min-w-0 flex-1', error && 'border-functional-failed03')}
                />
                {(rows.length > 1 || l.url) && (
                  <button
                    type='button'
                    aria-label='Remove link'
                    disabled={disabled}
                    onClick={(e) => motion.remove(i, e.currentTarget.closest('li'))}
                    className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[18px] text-text-secondary hover:bg-green-deep03/[0.06] hover:text-text-primary'
                  >
                    ×
                  </button>
                )}
              </div>
              {error && (
                <p
                  id={`${inputId}-error`}
                  role='alert'
                  className='ml-[164px] text-[12px] text-functional-failed03 max-sm:ml-[26px]'
                >
                  {error}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {rows.length < 10 && (
        <TdButton
          variant='ghost'
          size='s'
          disabled={disabled}
          onClick={() => motion.add(blankLink())}
          className='ml-6 w-fit border-dashed border-green-deep03/40 px-3 text-green-deep02 hover:bg-surface-green10 hover:text-green-deep02'
        >
          + Add link
        </TdButton>
      )}
    </div>
  );
};

/**
 * 결과물 제출 = 인계 제출 (1014 핸드오프 §2 · §3 · Resources 핸드오프 §4).
 *   인계 유형별 링크 슬롯(PD · DD · QA · FINAL) → Files(선택) → One-line summary → (FINAL 은 승인된 팀 링크 · 한 장 요약 4문항) → Task List 전부 ✓
 *   슬롯이 없는 인계(마케터 · 리더 → 팀장 일반)는 예전처럼 자유 링크 — 링크나 파일 중 하나 이상.
 *   다시 내면 "Submit new version · Submit v{n}", 최종 결과물이면 동료 평가 안내(재제출이면 기한이 다시 시작된다는 안내)
 */
export const SubmitModal = ({ task, onClose }) => {
  const ctx = useCommon(task);
  const { dispatch, memberById, now, displayTz, tz, toast } = ctx;
  const prev = task.deliverable ?? null;
  const [links, setLinks] = useState(() => (prev ? prevFreeLinks(prev) : [blankLink()]));
  // 인계 유형별 링크 칸 (PD · DD · QA · FINAL). 그 외는 자유 링크
  const ho = hoOf(
    task,
    memberById.get(assigneesOf(task)[0])?.job,
    memberById.get(task.receiverId)?.job,
  );
  const slots = ho ? HO_SLOTS[ho] : null;
  const [slotRows, setSlotRows] = useState(() =>
    prev ? prevSlotRows(prev, slots, REQUIRED_SLOTS[ho] ?? []) : initialSlotRows(slots),
  );
  const [onePage, setOnePage] = useState(() =>
    ONE_PAGE.map((item) => (prev?.onePage ?? []).find((p) => p.q === item.q)?.a ?? ''),
  );
  const [files, setFiles] = useState(() =>
    (prev?.files ?? []).map((f, i) => ({ ...f, prev: i })),
  );
  const [summary, setSummary] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const list = listOf(task, memberById);
  const items = list.full;
  const [checked, setChecked] = useState(() => items.map(() => false));
  const receiver = memberById.get(task.receiverId);
  const toNpc = receiver?.role === 'npc';
  const receiverName = toNpc ? 'Team lead Kim' : receiver?.name;
  const resubmit = !!task.deliverable;
  const nextVersion = resubmit ? (task.version ?? 1) + 1 : 1;
  const filled = slots ? filledSlots(slots, slotRows) : [];
  const freeLinks = links.filter((l) => l.url.trim());
  const cleanLinks = slots ? filled.map((x) => x.url) : freeLinks.map((l) => l.url.trim());
  const linkKinds = slots ? filled.map((x) => x.l) : freeLinks.map((l) => l.kind);
  const linksOk = slots
    ? slotRowsOk(slots, slotRows)
    : freeLinks.every((l) => !slotCheck(kindRule(l.kind), l.url));
  const hasArtifact = cleanLinks.length > 0 || files.length > 0;
  // 개발 → PM(QA) 은 배포 링크 · GitHub 저장소가 꼭 있어야 한다
  const missing = slots ? missingRequired(ho, slots, slotRows) : [];
  // 그 링크가 열리는지 · 저장소가 공개인지 · README 가 있는지 (A1 · A2) — 실패면 막고, 확인 중이면 기다린다
  const urlOf = (tag) => slotRows.find((r) => r.tag === tag && r.url.trim())?.url.trim() ?? '';
  const [linkCheck, setLinkCheck] = useState({ checking: false, result: null });
  const checkBlocks = ho === 'QA' && (linkCheck.checking || isBlocking(linkCheck.result));
  const onePageOk = ho !== 'FINAL' || onePage.every((a) => a.trim());
  // 항목이 하나도 없으면 체크할 것도 없다 — 서버도 빈 목록이면 통과시킨다 (제출이 영영 막히지 않게)
  const allChecked = checked.every(Boolean);
  const ok =
    hasArtifact &&
    linksOk &&
    !missing.length &&
    !checkBlocks &&
    summary.trim() &&
    onePageOk &&
    allChecked &&
    !sending;
  const reason = !linksOk
    ? 'Fix the links marked in red'
    : missing.length
      ? `Add the ${missing.join(' and ')}`
      : checkBlocks
        ? linkCheck.checking
          ? 'Checking your links…'
          : 'Fix the items marked ✕ in Link check'
        : !hasArtifact
          ? 'Add at least one link or file'
          : !summary.trim()
            ? 'Add a one-line summary'
            : !onePageOk
              ? 'Answer every one-page summary question'
              : !allChecked
                ? 'Check every Task List item'
                : '';
  const confirm = async () => {
    setSending(true);
    setError('');
    let attachments;
    try {
      attachments = await uploadAttachments(
        { live: ctx.live, slug: ctx.slug, taskId: task.id },
        files,
      );
    } catch {
      setSending(false);
      setError('Couldn’t upload the files. Check your connection and try again.');
      return;
    }
    dispatch({
      type: 'submit',
      taskId: task.id,
      link: cleanLinks[0] ?? null,
      links: cleanLinks,
      linkKinds,
      ...(slots ? { slots: filled } : {}),
      ...(ho === 'QA' && !ctx.live && linkCheck.result ? { checks: linkCheck.result } : {}),
      ...(ho === 'FINAL'
        ? { onePage: ONE_PAGE.map((item, i) => ({ q: item.q, a: onePage[i].trim() })) }
        : {}),
      files: attachments,
      summary: summary.trim(),
      items,
      at: now.toISOString(),
    });
    toast?.(
      task.isFinal
        ? resubmit
          ? {
              title: 'Final deliverable resubmitted',
              sub: 'Your team’s 3-hour peer review deadline restarts',
            }
          : { title: 'Final deliverable sent', sub: 'Peer review is open for your team' }
        : toNpc
          ? { title: 'Sent to team lead Kim', sub: 'Marked done. The team lead may send it back with notes.' }
          : resubmit
            ? {
                title: `v${nextVersion} sent`,
                sub: `${receiverName ?? 'The receiver'} gets notified to review`,
              }
            : {
                title: 'Handoff sent',
                sub: `${receiverName ?? 'The receiver'} gets notified to review`,
              },
    );
    onClose();
  };
  const title = task.isFinal
    ? 'Submit final deliverable'
    : resubmit
      ? 'Submit new version'
      : 'Submit deliverable';
  return (
    <Modal
      title={title}
      subtitle={`${task.title}${receiverName ? ` · to ${receiverName}` : ''}`}
      onClose={sending ? () => {} : onClose}
      width='max-w-[560px]'
      footer={
        <>
          {!ok && !sending && (
            <span className='mr-auto text-[13px] text-text-secondary'>{reason}</span>
          )}
          <ModalButton onClick={onClose} disabled={sending}>
            Cancel
          </ModalButton>
          <ModalButton primary disabled={!ok} onClick={confirm}>
            {sending ? 'Uploading…' : resubmit ? `Submit v${nextVersion}` : 'Submit'}
          </ModalButton>
        </>
      }
    >
      {resubmit ? (
        <p className='flex flex-col gap-1 rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
          <span>
            {ho === 'FINAL' ? 'Links, files and the one-page summary' : 'Links and files'} from v
            {nextVersion - 1} are filled in. Remove or replace only what changed, then submit as v
            {nextVersion}.
          </span>
          {task.isFinal && <span>Resubmitting restarts your team’s 3-hour peer review deadline.</span>}
        </p>
      ) : (
        task.isFinal && (
          <p className='rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
            Submitting opens peer review for your whole team. The 3-hour deadline starts now.
          </p>
        )
      )}
      {resubmit && (
        <div className='flex flex-col gap-1'>
          <span className='text-[13px] font-medium text-text-primary'>Builds on</span>
          <span className='text-[14px] text-text-secondary'>
            v{nextVersion - 1} · submitted {formatDue(prev.submittedAt, displayTz)} {tz}
          </span>
        </div>
      )}
      {slots ? (
        <SlotFields
          slots={slots}
          rows={slotRows}
          onChange={setSlotRows}
          disabled={sending}
          required={REQUIRED_SLOTS[ho] ?? []}
        />
      ) : (
        <Field
          label='Deliverable links'
          htmlFor='submit-link'
          hint='Add at least one link or file.'
        >
          <LinksEditor links={links} onChange={setLinks} disabled={sending} />
        </Field>
      )}
      {ho === 'QA' && (
        <LinkCheckPanel
          live={ctx.live}
          slug={ctx.slug}
          web={urlOf('web')}
          repo={urlOf('repo')}
          onState={setLinkCheck}
        />
      )}
      <Field label='Files' optional hint='Up to 10 files · 20 MB each'>
        <FileDrop files={files} onChange={setFiles} disabled={sending} />
      </Field>
      <Field label='One-line summary' required htmlFor='submit-summary'>
        <TextInput
          id='submit-summary'
          value={summary}
          disabled={sending}
          onChange={(e) => setSummary(e.target.value)}
        />
      </Field>
      {ho === 'FINAL' && (
        <OnePageFields answers={onePage} onChange={setOnePage} disabled={sending} />
      )}
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>
          Task List<span className='text-functional-failed03'>*</span>
        </span>
        <span className='text-[12px] text-text-secondary'>
          Check each item you included. The receiver checks them again.
        </span>
        <div className='flex flex-col'>
          {items.map((label, i) => (
            <React.Fragment key={label}>
              {list.base.length > 0 && (i === 0 || i === list.base.length) && (
                <span className='pb-0.5 pt-2 text-[12px] text-text-secondary'>
                  {i === 0 ? 'Base items' : 'Task items'}
                </span>
              )}
              <CheckRow
                label={`${i + 1}. ${label}`}
                checked={checked[i]}
                onChange={() => setChecked((c) => c.map((v, j) => (j === i ? !v : v)))}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
      {error && (
        <p role='alert' className='text-[13px] text-functional-failed04'>
          {error}
        </p>
      )}
    </Modal>
  );
};

/**
 * 막힘 요청 — 무엇이 막혔는지 + 도움 요청할 사람(여러 명, 선택). 고른 사람 모두에게 알림 · 태스크는 Blocked (W2_BlockerModal · §9)
 */
export const BlockerModal = ({ task, onClose }) => {
  const { dispatch, members, viewer, now, toast } = useCommon(task);
  const [body, setBody] = useState('');
  const [helpers, setHelpers] = useState([]);
  const people = members.filter(
    (m) => m.status === 'active' && m.role !== 'npc' && m.id !== viewer?.id,
  );
  const confirm = () => {
    dispatch({
      type: 'addComment',
      comment: {
        id: `c-${Date.now()}`,
        taskId: task.id,
        authorId: viewer?.id,
        type: 'blocker',
        targetUserId: helpers[0] ?? null,
        targetUserIds: helpers,
        body: body.trim(),
        createdAt: now.toISOString(),
        parentId: null,
      },
    });
    toast?.({
      title: 'Blocker raised',
      sub: helpers.length
        ? `${helpers.map((id) => people.find((m) => m.id === id)?.name).join(', ')} get${helpers.length === 1 ? 's' : ''} notified`
        : 'Your team sees it on the task',
    });
    onClose();
  };
  return (
    <Modal
      title='Raise blocker'
      subtitle={task.title}
      onClose={onClose}
      footer={
        <Footer
          onClose={onClose}
          label='Raise blocker'
          disabled={!body.trim()}
          onConfirm={confirm}
        />
      }
    >
      <Field label='What’s blocking you' required htmlFor='blocker-body'>
        <TextArea
          id='blocker-body'
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <fieldset className='flex flex-col gap-1'>
        <legend className='mb-1 text-[13px] font-medium text-text-primary'>
          Ask for help from <span className='font-normal text-text-secondary'>(optional)</span>
        </legend>
        <span className='mb-1 text-[12px] text-text-secondary'>
          Pick one or more. Everyone you pick gets notified.
        </span>
        {people.map((m) => (
          <CheckRow
            key={m.id}
            label={`${m.name} · ${JOB_LABEL[m.job] ?? m.job}`}
            checked={helpers.includes(m.id)}
            onChange={() =>
              setHelpers((h) => (h.includes(m.id) ? h.filter((x) => x !== m.id) : [...h, m.id]))
            }
          />
        ))}
      </fieldset>
    </Modal>
  );
};

/** 막힘 해소 — 도와준 사람을 고르면 기여자로 함께 기록된다 (MVP #6) */
export const ResolveBlockerModal = ({ task, onClose }) => {
  const { dispatch, members, viewer } = useCommon(task);
  const [how, setHow] = useState('helped');
  const [helper, setHelper] = useState('');
  const ok = how === 'self' || helper;
  const confirm = () => {
    dispatch({
      type: 'resolveBlocker',
      taskId: task.id,
      helpedBy: how === 'helped' ? helper : null,
    });
    onClose();
  };
  return (
    <Modal
      title='Resolve blocker'
      subtitle={task.title}
      onClose={onClose}
      footer={
        <Footer onClose={onClose} label='Resolve blocker' disabled={!ok} onConfirm={confirm} />
      }
    >
      <RadioGroup
        label='How was it resolved'
        required
        value={how}
        onChange={setHow}
        options={[
          { value: 'helped', label: 'Someone helped me' },
          { value: 'self', label: 'I solved it myself' },
        ]}
      />
      {how === 'helped' && (
        <Field label='Who helped you' required htmlFor='resolve-helper'>
          <PersonSelect
            id='resolve-helper'
            members={members}
            exclude={[viewer?.id]}
            value={helper}
            onChange={setHelper}
          />
        </Field>
      )}
    </Modal>
  );
};

/**
 * 예상 제출 시각 변경 — 늦추기만 가능 (MVP §8-9).
 * 늦추는 수정은 태스크당 1회만 지연 예고로 인정되고, 바꿀 때마다 기획자에게 알림이 간다.
 */
/** 마감 뒤로 미루면 — 더 강한 안내 (팀에 알리고 마무리 · 점수는 이미 깎임 · 리더와 운영진에게 알림) */
export const PastDueWarning = ({ due, displayTz, tz }) => (
  <div
    role='alert'
    className='flex flex-col gap-1 rounded-lg border border-functional-failed02 bg-functional-failed01 px-3 py-2.5 text-[13px] leading-[150%]'
  >
    <span className='font-medium text-functional-failed03'>
      This is after your due time ({formatDue(due, displayTz)} {tz})
    </span>
    <span className='text-text-primary'>
      A late submission already lowers your score. Tell your teammates where you are in a comment
      and wrap it up as soon as you can. Your leader and ops will be notified.
    </span>
  </div>
);

export const ExpectedTimeModal = ({ task, onClose }) => {
  const { dispatch, displayTz, tz, now, members, data, toast, due } = useCommon(task);
  const leaderName = members.find(isLeader)?.name;
  const current = task.expectedSubmitAt ? new Date(task.expectedSubmitAt) : null;
  const [value, setValue] = useState('');
  const at = fromInputValue(value, displayTz);
  const floor = current ?? now;
  // 늦추기만 · 프로젝트 마감까지
  const end = projectDeadline(data.project);
  const ok = at && at > floor && at <= end;
  const confirm = () => {
    dispatch({
      type: 'changeExpected',
      taskId: task.id,
      value: at.toISOString(),
      at: now.toISOString(),
    });
    toast?.({
      title: 'Expected submit time updated',
      sub: `${formatDue(at.toISOString(), displayTz)} ${tz} · the leader and receiver are notified`,
    });
    onClose();
  };
  return (
    <Modal
      title='Change expected submit time'
      subtitle={task.title}
      onClose={onClose}
      footer={<Footer onClose={onClose} label='Save' disabled={!ok} onConfirm={confirm} />}
    >
      <p className='rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
        Running late? Update this at least 30 min before the due time. Only your first update per
        task counts as an early notice. Later changes or missed times can lower your score.
      </p>
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Current</span>
        <span className='text-[14px] text-text-secondary'>
          {current ? `${formatDue(current, displayTz)} ${tz}` : 'Not set'}
        </span>
      </div>
      <Field
        label='New expected submit time'
        required
        htmlFor='expected-time'
        hint={
          value && !ok
            ? at && at > end
              ? `Pick a time before the project deadline (${formatDue(end, displayTz)} ${tz}).`
              : 'You can only move it later.'
            : `Later times only. ${leaderName ? `${leaderName} (leader)` : 'Your leader'} gets notified.`
        }
      >
        <DateTimeField
          id='expected-time'
          autoFocus
          value={value}
          min={toInputValue(floor, displayTz)}
          max={toInputValue(end, displayTz)}
          invalid={!!value && !ok}
          onChange={setValue}
        />
      </Field>
      {ok && at > due && <PastDueWarning due={due} displayTz={displayTz} tz={tz} />}
    </Modal>
  );
};

/** 기여자 추가 — 도움 받은 사람이 추가한다. 본인은 추가할 수 없다 (MVP #6 · §15) */
export const AddContributorModal = ({ task, onClose }) => {
  const { dispatch, members, viewer, data, memberById, displayTz, tz } = useCommon(task);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [commentId, setCommentId] = useState('');
  // 고른 기여자가 이 태스크에 쓴 댓글 · 답글만
  const comments = (data.comments ?? []).filter(
    (c) => c.taskId === task.id && userId && String(c.authorId) === String(userId),
  );
  const ok = userId && reason.trim();
  const confirm = () => {
    dispatch({
      type: 'addContributor',
      taskId: task.id,
      userId,
      reason: reason.trim(),
      commentId,
    });
    onClose();
  };
  return (
    <Modal
      title='Add contributor'
      subtitle={`${task.title} · Credit a teammate who helped. It shows in both of your Growth Reports.`}
      onClose={onClose}
      footer={
        <Footer onClose={onClose} label='Add contributor' disabled={!ok} onConfirm={confirm} />
      }
    >
      <Field label='Contributor' required htmlFor='contrib-person'>
        <PersonSelect
          id='contrib-person'
          members={members}
          exclude={[viewer?.id]}
          value={userId}
          onChange={(id) => {
            setUserId(id);
            // 다른 사람을 고르면 그 사람의 댓글로 다시 고른다
            setCommentId('');
          }}
        />
      </Field>
      <Field label='Reason' required htmlFor='contrib-reason'>
        <TextInput id='contrib-reason' value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field
        label='Related comment'
        optional
        htmlFor='contrib-comment'
        hint={
          !userId
            ? 'Pick a contributor first. You can link one of their comments or replies.'
            : comments.length === 0
              ? 'They haven’t commented on this task yet.'
              : 'Only their comments and replies on this task.'
        }
      >
        <Select
          id='contrib-comment'
          value={commentId}
          disabled={!userId || comments.length === 0}
          onChange={(e) => setCommentId(e.target.value)}
        >
          <option value=''>Select a comment</option>
          {comments.map((c) => (
            // 작성자 · 작성 시각 아래에 댓글 내용 (목록에서 2줄까지)
            <option key={c.id} value={c.id} data-sub={c.body}>
              {memberById.get(c.authorId)?.name ?? 'Team lead Kim'} ·{' '}
              {formatDue(c.createdAt, displayTz)} {tz}
            </option>
          ))}
        </Select>
      </Field>
    </Modal>
  );
};

export const markedLabels = (labels, slotLabels) =>
  labels.map((l) => (slotLabels.includes(l) ? `Link slot · ${l}` : l));

/** 반려 — 페이지에서 ✗ 한 항목 + 사유(5종, 라디오) + 설명 (MVP §5 · §6-5 · W3_RejectModal) */
export const RejectModal = ({ task, failedItems, passedItems = [], slotLabels = [], onClose }) => {
  const { dispatch, now, memberById, toast } = useCommon(task);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const ok = failedItems.length > 0 && reason && description.trim();
  const confirm = () => {
    dispatch({
      type: 'reject',
      taskId: task.id,
      failedItems,
      items: passedItems,
      reason,
      description: description.trim(),
      at: now.toISOString(),
    });
    toast?.({
      title: 'Handoff rejected',
      sub: `${assigneesOf(task)
        .map((id) => memberById.get(id)?.name)
        .filter(Boolean)
        .join(', ')} notified`,
    });
    onClose();
  };
  return (
    <Modal
      title='Reject handoff'
      subtitle={`${task.title} · v${task.version ?? 1} from ${memberById.get(task.assigneeId)?.name ?? ''}`}
      onClose={onClose}
      width='max-w-[520px]'
      footer={<Footer onClose={onClose} label='Reject' disabled={!ok} onConfirm={confirm} />}
    >
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Items marked ✗</span>
        <span className='text-[14px] text-text-secondary'>
          {markedLabels(failedItems, slotLabels).join(', ')}
        </span>
      </div>
      <RadioGroup
        label='Reason'
        required
        value={reason}
        onChange={setReason}
        options={rejectReasonsFor(task, memberById).map((r) => ({ value: r.key, label: r.label }))}
      />
      <Field
        label='Description'
        required
        htmlFor='reject-desc'
        hint='Say what to change so the next version can pass.'
      >
        <TextArea
          id='reject-desc'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

/**
 * 되돌림 — 승인한 뒤 담당자에게 단 열린 질문을 근거로 다시 작업하게 한다 (W3_ReturnModal).
 * 연결할 질문이 없으면 버튼이 꺼져 있다 (페이지에서).
 */
export const ReturnModal = ({ task, questions, onClose }) => {
  const { dispatch, now, memberById, displayTz, tz, toast } = useCommon(task);
  const [commentId, setCommentId] = useState(questions.length === 1 ? questions[0].id : '');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const ok = commentId && reason && description.trim();
  const assigneeName = assigneesOf(task)
    .map((id) => memberById.get(id)?.name)
    .filter(Boolean)
    .join(', ');
  const confirm = () => {
    dispatch({
      type: 'returnHandoff',
      taskId: task.id,
      commentId,
      reason,
      description: description.trim(),
      at: now.toISOString(),
    });
    toast?.({ title: 'Returned to sender', sub: `${assigneeName} gets notified` });
    onClose();
  };
  return (
    <Modal
      title='Return to sender'
      subtitle={`${task.title} · v${task.version ?? 1} from ${assigneeName}`}
      onClose={onClose}
      width='max-w-[520px]'
      footer={
        <Footer onClose={onClose} label='Return to sender' disabled={!ok} onConfirm={confirm} />
      }
    >
      <Field label='Linked question' required htmlFor='return-question'>
        <Select
          id='return-question'
          value={commentId}
          onChange={(e) => setCommentId(e.target.value)}
        >
          <option value=''>Select a question</option>
          {questions.map((q) => (
            <option key={q.id} value={q.id}>
              {memberById.get(q.authorId)?.name} · {formatDue(q.createdAt, displayTz)} {tz} ·
              Question to {assigneeName}
            </option>
          ))}
        </Select>
      </Field>
      <RadioGroup
        label='Reason'
        required
        value={reason}
        onChange={setReason}
        options={rejectReasonsFor(task, memberById).map((r) => ({ value: r.key, label: r.label }))}
      />
      <Field label='Description' required htmlFor='return-desc'>
        <TextArea
          id='return-desc'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

/** 원본 댓글 고르기 (선택) — 재배정·범위 변경의 근거 */
const CommentPicker = ({ id, task, value, onChange }) => {
  const { data, memberById, displayTz, tz } = useCommon(task);
  const comments = (data.comments ?? []).filter((c) => c.taskId === task.id && !c.parentId);
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value=''>Select a comment</option>
      {comments.map((c) => (
        <option key={c.id} value={c.id} data-sub={c.body}>
          {memberById.get(c.authorId)?.name ?? 'Team lead Kim'} ·{' '}
          {formatDue(c.createdAt, displayTz)} {tz}
        </option>
      ))}
    </Select>
  );
};

/**
 * 리더 재배정 — 새 담당자 · 사유 · 원본 댓글(선택). 무응답이면 사유 기본값 "No response to acceptance" (W5_ReassignModal)
 */
export const ReassignModal = ({ task, onClose }) => {
  const { dispatch, members, now, memberById, toast } = useCommon(task);
  const noResponse = task.state === 'assigned';
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState(() =>
    task.state === 'declined'
      ? `${memberById.get(task.assigneeId)?.name ?? 'Assignee'} declined: ${task.declineReason ?? ''}`.trim()
      : noResponse
        ? 'No response to acceptance'
        : '',
  );
  const [commentId, setCommentId] = useState('');
  const ok = userId && reason.trim();
  const confirm = () => {
    dispatch({
      type: 'reassign',
      taskId: task.id,
      userId,
      reason: reason.trim(),
      commentId,
      at: now.toISOString(),
    });
    toast?.({
      title: 'Task reassigned',
      sub: `${memberById.get(userId)?.name ?? 'The new assignee'} gets notified`,
    });
    onClose();
  };
  return (
    <Modal
      title='Reassign task'
      subtitle={task.title}
      onClose={onClose}
      footer={<Footer onClose={onClose} label='Reassign' disabled={!ok} onConfirm={confirm} />}
    >
      <Field label='New assignee' required htmlFor='reassign-person'>
        <PersonSelect
          id='reassign-person'
          members={members}
          exclude={[
            task.assigneeId,
            task.receiverId,
            ...members.filter((m) => m.job === 'marketer').map((m) => m.id),
          ]}
          value={userId}
          onChange={setUserId}
          withJob
        />
      </Field>
      <Field label='Reason' required htmlFor='reassign-reason'>
        <TextArea id='reassign-reason' value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field label='Source comment' optional htmlFor='reassign-comment'>
        <CommentPicker
          id='reassign-comment'
          task={task}
          value={commentId}
          onChange={setCommentId}
        />
      </Field>
    </Modal>
  );
};

/** 리더 받는 사람 변경 */
/**
 * 지울 수 있는 태스크 — 한 번도 제출되지 않았고, PM 이 Add task 로 만들었거나(코드 없음)
 * 담당자가 거절했다 (운영진이 만든 태스크도 거절되면 지우거나 담당자를 바꿀 수 있다)
 */
export const canDeleteTask = (task) =>
  (!task.code || task.state === 'declined') &&
  !task.deliverable &&
  !(task.history ?? []).length &&
  ['assigned', 'in_progress', 'declined'].includes(task.state);

const DELETE_REASON_MAX = 300;

/** 태스크 지우기 — 사유(선택)와 함께 담당자에게 알림, 댓글도 함께 지워진다 */
export const DeleteTaskModal = ({ task, onClose, onDeleted }) => {
  const { dispatch, now, toast } = useCommon(task);
  const [reason, setReason] = useState('');
  const confirm = () => {
    dispatch({ type: 'deleteTask', taskId: task.id, reason: reason.trim(), at: now.toISOString() });
    toast?.({ title: 'Task deleted', sub: 'The assignees get notified' });
    onClose();
    onDeleted?.();
  };
  return (
    <Modal
      title={`Delete “${task.title}”?`}
      subtitle={
        task.state === 'declined'
          ? 'It was declined, so you can delete it instead of picking a new assignee. Its comments are deleted too. This can’t be undone.'
          : 'It hasn’t been submitted yet, so it’s removed from the board. Its comments are deleted too. This can’t be undone.'
      }
      onClose={onClose}
      footer={
        <>
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <TdButton variant='danger' onClick={confirm}>
            Delete task
          </TdButton>
        </>
      }
    >
      <Field
        label='Reason (optional)'
        htmlFor='delete-reason'
        hint='The assignees see this in their notification.'
      >
        <TextArea
          id='delete-reason'
          autoFocus
          maxLength={DELETE_REASON_MAX}
          placeholder='e.g. Merged into P2'
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

export const ChangeReceiverModal = ({ task, onClose }) => {
  const { dispatch, members, now, memberById, toast } = useCommon(task);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const ok = userId && reason.trim();
  const people = members.filter(
    (m) =>
      m.status === 'active' &&
      m.job !== 'marketer' &&
      !assigneesOf(task).includes(m.id) &&
      m.id !== task.receiverId,
  );
  const confirm = () => {
    dispatch({
      type: 'changeReceiver',
      taskId: task.id,
      userId,
      reason: reason.trim(),
      at: now.toISOString(),
    });
    toast?.({
      title: 'Receiver changed',
      sub: `${memberById.get(userId)?.name ?? 'The new receiver'} gets notified`,
    });
    onClose();
  };
  return (
    <Modal
      title='Change receiver'
      subtitle={task.title}
      onClose={onClose}
      footer={
        <Footer onClose={onClose} label='Change receiver' disabled={!ok} onConfirm={confirm} />
      }
    >
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Current receiver</span>
        <span className='text-[14px] text-text-secondary'>
          {memberById.get(task.receiverId)?.name ?? 'Team lead Kim'}
        </span>
      </div>
      <Field
        label='New receiver'
        required
        htmlFor='receiver-person'
        hint='Assignees can’t review their own task.'
      >
        <Select id='receiver-person' value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value=''>Select a person</option>
          {people.map((m) => (
            <option key={m.id} value={m.id}>
              {m.role === 'npc' ? 'Team lead Kim' : `${m.name} · ${JOB_LABEL[m.job] ?? m.job}`}
            </option>
          ))}
        </Select>
      </Field>
      <Field label='Reason' required htmlFor='receiver-reason'>
        <TextArea id='receiver-reason' value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
};

/** 리스트가 바뀐 뒤 예상 제출 시각 다시 정하기 */
export const ResetExpectedModal = ({ task, onClose }) => {
  const { dispatch, displayTz, tz, now, due, data, members, toast } = useCommon(task);
  const leaderName = members.find(isLeader)?.name;
  const current = task.expectedSubmitAt ? new Date(task.expectedSubmitAt) : null;
  const end = expectedCapOf(due, now, data);
  const [value, setValue] = useState('');
  const at = fromInputValue(value, displayTz);
  const ok = at && at > now && at <= end;
  const confirm = () => {
    dispatch({
      type: 'resetExpected',
      taskId: task.id,
      value: at.toISOString(),
      at: now.toISOString(),
    });
    toast?.({
      title: 'Expected submit time updated',
      sub: `${formatDue(at.toISOString(), displayTz)} ${tz} · the leader and receiver are notified`,
    });
    onClose();
  };
  return (
    <Modal
      title='Reset expected submit time'
      subtitle={task.title}
      onClose={onClose}
      footer={<Footer onClose={onClose} label='Save' disabled={!ok} onConfirm={confirm} />}
    >
      <p className='rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
        The Task List changed after you accepted. You can set a new time once. It doesn’t count as a
        delay notice.
      </p>
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Current</span>
        <span className='text-[14px] text-text-secondary'>
          {current ? `${formatDue(current, displayTz)} ${tz}` : 'Not set'}
        </span>
      </div>
      <Field
        label='New expected submit time'
        required
        htmlFor='reset-time'
        hint={
          value && !ok
            ? 'Pick a time before the due time.'
            : `Earlier or later, up to the due time (${formatDue(end, displayTz)} ${tz}). ${
                leaderName ? `${leaderName} (leader)` : 'Your leader'
              } gets notified.`
        }
      >
        <DateTimeField
          id='reset-time'
          autoFocus
          value={value}
          min={toInputValue(now, displayTz)}
          max={toInputValue(end, displayTz)}
          invalid={!!value && !ok}
          onChange={setValue}
        />
      </Field>
    </Modal>
  );
};

/** 리더 범위 변경 — 지금 Task List(읽기 전용) · 과제 항목 편집 · 사유 · 원본 댓글 (W5_ScopeChangeModal) */
export const ScopeChangeModal = ({ task, onClose }) => {
  const { dispatch, now, due, data, memberById, displayTz, tz } = useCommon(task);
  const list = listOf(task, memberById);
  const teamStart = new Date(
    data.project.teamStartAt ?? new Date(data.project.startAt).getTime() + 3_600_000,
  );
  const rule = scopeRule(task, now, due, teamStart);
  const [rows, setRows] = useState(() =>
    list.items.map((text, i) => ({ text, origin: i, source: list.sources?.[i] ?? 'ops' })),
  );
  const [reason, setReason] = useState('');
  const [commentId, setCommentId] = useState('');
  const kept = rows.filter((r) => r.text.trim());
  const clean = kept.map((r) => r.text.trim());
  const changed = clean.join('\n') !== list.items.join('\n');
  const enough = list.base.length + clean.length > 0;
  const onlyRemoved = !rule.deleteOnly || kept.every((r) => r.origin != null);
  const ok = rule.open && enough && reason.trim() && changed && onlyRemoved;
  const tagOf = (r) => {
    if (!list.sources) return null;
    if (r.origin == null) return SOURCE_LABEL.pm;
    const edited = r.text.trim() !== list.items[r.origin];
    return SOURCE_LABEL[edited && r.source === 'ops' ? 'pm_edit' : r.source];
  };
  const confirm = () => {
    dispatch({
      type: 'changeScope',
      taskId: task.id,
      items: clean,
      origins: kept.map((r) => r.origin),
      reason: reason.trim(),
      commentId,
      at: now.toISOString(),
    });
    onClose();
  };
  return (
    <Modal
      title='Change scope'
      subtitle={
        rule.free
          ? `${task.title} · leader only · no notifications before team start (${formatClock(teamStart, KST)} KST)`
          : task.title
      }
      onClose={onClose}
      width='max-w-[560px]'
      footer={
        <>
          {!ok && (
            <span className='mr-auto text-[13px] text-text-secondary'>
              {!rule.open
                ? 'Locked'
                : !enough
                  ? 'Add at least one Task List item'
                  : !onlyRemoved
                    ? 'You can only remove items now'
                    : !changed
                      ? 'Change the Task List'
                      : 'Add a reason'}
            </span>
          )}
          <ModalButton onClick={onClose}>Cancel</ModalButton>
          <ModalButton primary disabled={!ok} onClick={confirm}>
            Save change
          </ModalButton>
        </>
      }
    >
      {rule.free && (
        <p className='rounded-lg bg-grayscale-gray01 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
          ⓘ Members aren’t notified until the team joins at {formatClock(teamStart, KST)} KST.
        </p>
      )}
      {!rule.open && (
        <p className='rounded-lg bg-functional-warning01 px-3 py-2.5 text-[13px] leading-[150%] text-functional-warning04'>
          {rule.why}
        </p>
      )}
      {rule.open && rule.deleteOnly && (
        <p className='rounded-lg bg-functional-warning01 px-3 py-2.5 text-[13px] leading-[150%] text-functional-warning04'>
          Less than 1 hour to the due time. You can only remove items now.
        </p>
      )}
      {rule.open && !rule.deleteOnly && task.state === 'rework_requested' && (
        <p className='rounded-lg bg-surface-green10 px-3 py-2.5 text-[13px] leading-[150%] text-text-primary'>
          Changes were requested. You can add or remove items, but not reword them.
        </p>
      )}
      <div className='flex flex-col gap-1'>
        <span className='text-[13px] font-medium text-text-primary'>Due</span>
        <span className='text-[14px] text-text-secondary'>
          {formatDue(due, displayTz)} {tz}
        </span>
        <span className='text-[12px] text-text-secondary'>
          {rule.free
            ? 'Set due times in Adjust timeline before your team starts.'
            : 'Only ops can change the due time.'}
        </span>
      </div>
      <div className='flex flex-col gap-1.5'>
        <span className='text-[13px] font-medium text-text-primary'>Current Task List</span>
        <TaskListGroups
          type={list.type}
          base={list.base}
          items={list.items}
          sources={list.sources}
        />
      </div>
      {rule.open && (
        <Field
          label='Task items'
          htmlFor='scope-list'
          hint='One item per row · Enter adds the next.'
        >
          <TaskListEditor
            id='scope-list'
            items={rows}
            onChange={setRows}
            startAt={list.base.length}
            placeholder={list.base.length ? 'Add a task item (optional)' : undefined}
            textOf={(r) => r.text}
            withText={(r, v) => ({ ...r, text: v })}
            blankRow={{ text: '', origin: null, source: 'pm' }}
            isLocked={(r) => r.origin != null && rule.keepText}
            tagOf={tagOf}
            canAdd={!rule.deleteOnly}
          />
        </Field>
      )}
      <Field label='Reason' required htmlFor='scope-reason'>
        <TextArea id='scope-reason' value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Field label='Source comment' optional htmlFor='scope-comment'>
        <CommentPicker id='scope-comment' task={task} value={commentId} onChange={setCommentId} />
      </Field>
    </Modal>
  );
};
